/** item 7 · PWA 公司會議室（實體會議·單一裝置錄整場）。主席開始/結束+指定紀錄人員；
 *  錄音裝置（紀錄人員或主席）自動連續辨識+上傳 room 逐字稿；其他與會者只報到看出席。*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';

import { useMessage } from '../../../contexts/MessageContext';
import { useContinuousSpeech } from '../../../hooks/useContinuousSpeech';
import {
  addChunk, checkIn, checkOut, getMeeting, liveAttendees, LiveAttendee, OfficeMeeting,
  startRecording, stopRecording,
} from '../officeMeeting/services/officeMeetingService';

const CHUNK_MS = 30000;
const POLL_MS = 5000;
const sttLangOf = (lang: string): string =>
  (lang === 'zh-CN' ? 'zh-CN' : lang === 'en' ? 'en-US' : 'zh-TW');

const OfficePwaRoomPage: React.FC = () => {
  const { id } = useParams();
  const mid = Number(id);
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const tt = (k: string, d: string) => t(`officeMeeting.${k}`, d);
  const { showApiError, showSuccess } = useMessage();
  const { supported, recording, interim, start, stop } = useContinuousSpeech(sttLangOf(i18n.language));

  const [m, setM] = useState<OfficeMeeting | null>(null);
  const [live, setLive] = useState<LiveAttendee[]>([]);
  const [startDlg, setStartDlg] = useState(false);
  const [recorderId, setRecorderId] = useState<number | ''>('');
  const bufRef = useRef<string[]>([]);
  const chunkIdxRef = useRef(0);
  const flushTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const inProgress = m?.status === 'in_progress';
  const isChair = !!m?.is_chair;
  const canRecord = !!m?.can_record;

  const refreshMeeting = useCallback(async () => {
    try { setM(await getMeeting(mid)); } catch (err) { showApiError(err); }
  }, [mid, showApiError]);
  const refreshLive = useCallback(async () => {
    try { setLive(await liveAttendees(mid)); } catch { /* silent ok: 輪詢出席失敗 */ }
  }, [mid]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await refreshMeeting();
      try { await checkIn(mid, navigator.userAgent); } catch (err) { showApiError(err); }
      if (mounted) refreshLive();
    })();
    pollTimer.current = setInterval(refreshLive, POLL_MS);
    return () => {
      mounted = false;
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (flushTimer.current) clearInterval(flushTimer.current);
      try { stop(); } catch { /* silent ok: STT 可能已停·重複呼叫無害 */ }
      checkOut(mid).catch(() => undefined);   // silent ok: 離場 check-out 失敗不阻擋導航
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  const flush = useCallback(async () => {
    const text = bufRef.current.join(' ').trim();
    if (!text) return;
    bufRef.current = [];
    try { await addChunk(mid, chunkIdxRef.current++, text, CHUNK_MS); }
    catch { /* silent ok: 單段上傳失敗·下段續傳 */ }
  }, [mid]);

  useEffect(() => {
    if (inProgress && canRecord && supported && !recording) {
      start((ft) => { bufRef.current.push(ft); });
      if (!flushTimer.current) flushTimer.current = setInterval(flush, CHUNK_MS);
    }
    if (!inProgress && recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, canRecord, supported]);

  const doStart = async () => {
    try {
      await startRecording(mid, recorderId === '' ? null : recorderId);
      setStartDlg(false); await refreshMeeting();
      showSuccess('SYS020001', { name: tt('recording', '會議錄音') });
    } catch (err) { showApiError(err); }
  };
  const doStop = async () => {
    stop();
    if (flushTimer.current) { clearInterval(flushTimer.current); flushTimer.current = null; }
    await flush();
    try { await stopRecording(mid); showSuccess('SYS020001', { name: tt('minutes', '會議記錄') }); nav(`/ee_pwa_minutes/${mid}`); }
    catch (err) { showApiError(err); }
  };

  if (!m) return <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;
  const present = live.filter((a) => a.present);
  const openStartDlg = () => { setRecorderId(m.recorder_user_id ?? ''); setStartDlg(true); };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Button size="small" onClick={() => nav('/ee_pwa_office_meetings')}>← {tt('backList', '返回清單')}</Button>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{m.title}</Typography>
        <Chip size="small" color={inProgress ? 'warning' : 'default'} label={tt(`status_${m.status}`, m.status)} />
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('liveAttendees', '目前在會議室')}（{present.length}）</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {present.map((a) => <Chip key={a.user_id} size="small"
            color={m.recording_user_id === a.user_id ? 'error' : 'default'} label={a.name} />)}
          {present.length === 0 && <Typography variant="body2" color="text.secondary">{tt('nobody', '（尚無人在線）')}</Typography>}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        {inProgress && m.recording_user_name && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {tt('recordingBy', '會議進行中 · 由')} {m.recording_user_name} {tt('recordingBySuffix', '記錄')}
          </Alert>
        )}
        {inProgress && canRecord && !supported && (
          <Alert severity="warning" sx={{ mb: 1 }}>{tt('noStt', '此瀏覽器不支援語音辨識')}</Alert>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isChair && !inProgress && m.status === 'scheduled' && (
            <Button variant="contained" color="warning" fullWidth onClick={openStartDlg}>{tt('startRec', '開始會議')}</Button>
          )}
          {isChair && inProgress && (
            <Button variant="outlined" color="error" fullWidth onClick={doStop}>{tt('stopRec', '結束會議並產記錄')}</Button>
          )}
        </Stack>
        {canRecord && recording && (
          <Box sx={{ mt: 1.5, bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
            <Typography variant="caption" color="error">● {tt('capturing', '錄音中')}</Typography>
            {interim && <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>{interim}</Typography>}
          </Box>
        )}
      </Paper>

      <Dialog open={startDlg} onClose={() => setStartDlg(false)} fullWidth maxWidth="xs">
        <DialogTitle>{tt('startTitle', '開始會議')}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth sx={{ mt: 1 }} label={tt('recorder', '紀錄人員')}
            value={recorderId === '' ? '' : recorderId}
            onChange={(e) => setRecorderId(e.target.value === '' ? '' : Number(e.target.value))}
            helperText={tt('recorderHint2', '由此人的裝置錄整場；未指定則由主席的裝置')}>
            <MenuItem value="">{tt('recorderSelf', '（主席自己）')}</MenuItem>
            {(m.attendees || []).map((a) => <MenuItem key={a.user_id} value={a.user_id}>{a.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDlg(false)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" onClick={doStart}>{tt('startConfirm', '開始')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfficePwaRoomPage;
